<template>
  <Steps
    ref="formWizard"
    :steps="formSteps"
    :is-submitting="purchaseNumberApiRequest.isLoading.value"
    @before-move="validateStep"
    @on-complete="submitNewNumber"
  >
    <template #step-1>
      <FormGroup
        :label="$t('ui.forms.displayName')"
        :is-required="v$.name.required"
      >
        <div class="text-quaternary text-sm mb-1">{{ $t('ui.forms.displayNameFormat') }}</div>
        <Input
          v-model="newNumber.name"
          :has-error="v$.name.$error"
          placeholder="Vindis Audi Cambridge"
        />
        <InputError :has-error="v$.name.$error">{{ v$.name.$errors[0]?.$message }}</InputError>
      </FormGroup>
      <FormGroup
        :label="$t('ui.forms.textToSpeechName')"
        :is-required="v$.textToSpeechName.required"
      >
        <div class="text-quaternary text-sm">{{ $t('ui.forms.textToSpeechHint') }}</div>
        <div class="text-quaternary text-sm mb-1">{{ $t('ui.forms.textToSpeechWarning') }}</div>

        <Input
          v-model="newNumber.textToSpeechName"
          :has-error="v$.textToSpeechName.$error"
          icon="comment"
          placeholder="Group Seyut Maidenhead"
        />
        <InputError :has-error="v$.textToSpeechName.$error">{{ v$.textToSpeechName.$errors[0]?.$message }}</InputError>
      </FormGroup>

      <FormGroup
        :label="$t('ui.entities.departments.callCenter')"
        :is-required="v$.callCenterId.required"
      >
        <Select
          v-model="newNumber.callCenterId"
          :options="callCentersList"
          value-key="id"
          label-key="name"
          :class="{ 'has-error': v$.callCenterId.$error }"
        />

        <InputError :has-error="v$.callCenterId.$error">{{ v$.callCenterId.$errors[0]?.$message }}</InputError>
      </FormGroup>

      <FormGroup
        :label="$t('ui.forms.approvedBundleSid')"
        :is-required="v$.bundleSid.required"
      >
        <Select
          v-model="newNumber.bundleSid"
          :options="bundleOptions"
          :has-error="v$.bundleSid.$error"
          value-key="sid"
          label-key="name"
        >
          <template #value="{ value }">
            <div class="flex items-center gap-2">
              {{ value.name }}
            </div>
          </template>
          <template #option="{ option }">
            <div>
              {{ option.name }}
              <div class="text-sm text-quaternary">{{ option.sid }}</div>
            </div>
          </template>
        </Select>
        <InputError :has-error="v$.bundleSid.$error">{{ v$.bundleSid.$errors[0]?.$message }}</InputError>
      </FormGroup>

      <FormGroup
        :label="$t('ui.forms.addressSid')"
        :is-required="v$.addressSid.required"
      >
        <Select
          v-model="newNumber.addressSid"
          :has-error="v$.addressSid.$error"
          :options="addressOptions"
          value-key="sid"
          label-key="name"
        >
          <template #value="{ value }">
            <div>{{ value.name }}</div>
          </template>
          <template #option="{ option }">
            <div>
              <div>{{ option.name }}</div>
              <div class="text-sm text-quaternary">({{ option.address }})</div>
              <div class="text-sm text-quaternary">{{ option.sid }}</div>
            </div>
          </template>
        </Select>
        <InputError :has-error="v$.addressSid.$error">{{ v$.addressSid.$errors[0]?.$message }}</InputError>
      </FormGroup>
    </template>

    <template #step-2>
      <div class="flex items-start gap-4 mb-6">
        <FormGroup
          :label="$t('ui.entities.address.country')"
          :is-required="v$Search.country.required"
          no-margin
          class="w-[50%]"
        >
          <Select
            v-model="searchData.country"
            :options="countriesList"
            value-key="code"
            label-key="name"
            searchable
          >
            <template #value="{ value }">
              <div class="flex items-center gap-2">
                <img
                  :src="value.image"
                  width="20"
                />
                (+{{ value.dialCode }}) {{ value.name }}
              </div>
            </template>
            <template #option="{ option }">
              <div class="flex items-center gap-2">
                <img
                  :src="option.image"
                  width="20"
                />
                (+{{ option.dialCode }}) {{ option.name }}
              </div>
            </template>
          </Select>
          <InputError :has-error="v$Search.country.$error">{{ v$Search.country.$errors[0]?.$message }}</InputError>
        </FormGroup>
        <FormGroup
          :label="$t('ui.forms.numberStartsWith')"
          no-margin
          :is-required="v$Search.number.required"
        >
          <Input
            v-model="searchData.number"
            :has-error="v$Search.number.$error"
          />
          <InputError :has-error="v$Search.number.$error">{{ v$Search.number.$errors[0]?.$message }}</InputError>
        </FormGroup>
        <div class="mt-7">
          <Button
            :label="$t('ui.actions.search')"
            :is-loading="purchasableNumbersApiRequest.isLoading.value"
            outlined
            @click="searchNumber"
          />
        </div>
      </div>
      <div class="w-full">
        <div
          v-if="purchasablePhoneNumbers"
          class="flex px-4"
        >
          <Label class="w-[30%]">{{ $t('ui.forms.number') }}</Label>
          <Label class="w-[15%]">{{ $t('ui.forms.voice') }}</Label>
          <Label class="w-[15%]">{{ $t('ui.forms.sms') }}</Label>
          <Label>{{ $t('ui.forms.location') }}</Label>
        </div>
        <ListGroup>
          <ListGroupItem v-if="purchasableNumbersApiRequest.isLoading.value">
            <div class="flex flex-col justify-center items-center p-4">
              <Icon
                type="spinner-third"
                class="btn-icon fa-spin"
                fa-style="far"
              />
              <div class="text-quaternary">{{ $t('ui.messages.loadingResults') }}</div>
            </div>
          </ListGroupItem>
          <div v-else-if="purchasablePhoneNumbers">
            <ListGroupItem
              v-for="(result, index) in purchasablePhoneNumbers"
              :key="index"
              class="flex gap-2 items-center link-hover"
              :class="{ 'text-brand-500': selectedNumberIndex == index }"
              @click="selectNumber(index)"
            >
              <div class="flex w-full items-center">
                <div class="flex w-full">
                  <div class="w-[35%]">{{ result.friendlyName }}</div>
                  <div
                    v-tippy="$t('ui.forms.canMakeCalls')"
                    class="flex w-[15%]"
                  >
                    <Icon
                      :type="result.capabilities.voice ? 'check' : 'xmark'"
                      :severity="result.capabilities.voice ? '' : 'error'"
                    />
                  </div>
                  <div
                    v-tippy="$t('ui.forms.canSendSms')"
                    class="flex w-[15%]"
                  >
                    <Icon
                      :type="result.capabilities.sms ? 'check' : 'xmark'"
                      :severity="result.capabilities.sms ? '' : 'error'"
                    />
                  </div>
                  <div class="flex w-[45%] justify-between">
                    <div class="text-sm">{{ result.locality }}</div>
                    <!-- <Icon v-if="selectedNumberIndex == index" type="check" /> -->
                    <div v-if="selectedNumberIndex == index">{{ $t('ui.messages.selected') }}</div>
                  </div>
                </div>
              </div>
            </ListGroupItem>
          </div>

          <ListGroupItem v-else>
            <div class="flex flex-col justify-center items-center p-4">
              <Icon
                type="search"
                class="text-xl"
              />
              <div class="text-quaternary">{{ $t('ui.messages.noResults') }}</div>
            </div>
          </ListGroupItem>
        </ListGroup>

        <div
          v-if="purchasablePhoneNumbers"
          class="link-hover text-secondary flex gap-2 items-center justify-center mt-4 w-full"
          @click="searchNumber"
        >
          <Icon type="arrows-rotate" />
          {{ $t('ui.messages.refreshResults') }}
        </div>

        <InputError :has-error="v$.phoneNumberIso.$error">{{ v$.phoneNumberIso.$errors[0]?.$message }}</InputError>
      </div>
    </template>

    <template #step-3>
      <div class="text-xl mb-2">{{ newNumber.name }}</div>
      <div class="flex justify-between items-center mb-6">
        <Chip>
          <img
            :src="countriesList.find(country => country.dialCode == newNumber.dialingCode).image"
            width="20"
            class="mr-2"
          />
          {{ newNumber.location }},
          {{ countriesList.find(country => country.dialCode == newNumber.dialingCode).country }}
        </Chip>
        <div class="text-xl">{{ newNumber.phoneNumberIso }}</div>
      </div>

      <div class="max-w-96">
        <div class="text-sm mb-2">{{ $t('ui.entities.dealership', 2) }}</div>
        <div class="flex flex-wrap gap-2">
          <div
            v-for="(dealership, index) in newNumber.dealerships"
            :key="index"
          >
            <Chip size="sm">{{ dealershipsList.find(d => d.value == dealership)?.label }}</Chip>
          </div>
        </div>
      </div>
    </template>
  </Steps>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import Steps from '@/components/steps/Steps.vue'

import Icon from '@/components/icon/Icon.vue'
import FormGroup from '@/components/forms/FormGroup.vue'
import Input from '@/components/forms/Input.vue'
import InputError from '@/components/forms/InputError.vue'
import { alertToast } from '@/utilities/notification'
import Select from '@/components/forms/Select.vue'
import Button from '@/components/button/Button.vue'
import ListGroup from '@/components/list/ListGroup.vue'
import ListGroupItem from '@/components/list/ListGroupItem.vue'
import Chip from '@/components/chip/Chip.vue'
import Label from '@/components/forms/Label.vue'

import useVuelidate from '@vuelidate/core'
import { required, numeric, helpers } from '@vuelidate/validators'
import { useI18n } from 'vue-i18n'
import useApiRequest from '@/composables/useApiRequest'
import Swal from 'sweetalert2'

const emit = defineEmits(['form-updated', 'is-submitting', 'completed'])
const { t } = useI18n()

const purchasableNumbersApiRequest = useApiRequest()
const purchaseNumberApiRequest = useApiRequest()

const formWizard = ref(null) // Reference to the FormWizard component

const callCentersList = ref([])
const callCentersApiRequest = useApiRequest()

try {
  callCentersApiRequest.send({ method: 'GET', endpoint: '/v1/call-centers' }).then(response => {
    if (response.success) {
      callCentersList.value = response.data
    } else {
      alertToast(
        t('ui.messages.failedToFetch', { item: t('ui.entities.dashboards.callCenter', 2) }),
        response.message,
        'error'
      )
    }
  })
} catch (err) {
  alertToast(t('ui.messages.failedToFetch', { item: t('ui.entities.dashboards.callCenter', 2) }), err.message, 'error')
}

const formSteps = computed(() => [
  {
    label: t('ui.forms.basicDetails'),
    icon: null
  },
  {
    label: t('ui.forms.searchNumber'),
    icon: null
  },
  {
    label: t('ui.forms.summary'),
    icon: null
  }
])

const newNumber = ref({
  name: null,
  textToSpeechName: null,
  callCenterId: null,
  dialingCode: null,
  country: null,
  number: null,
  phoneNumberIso: null,
  location: null,
  addressSid: null,
  bundleSid: null,
  canMakeCalls: null,
  canSendSms: null
})

const purchasablePhoneNumbers = ref(null)
const selectedNumberIndex = ref(null)

const inputRules = {
  formStep1: {
    name: { required },
    textToSpeechName: { required },
    callCenterId: { required },
    bundleSid: { required },
    addressSid: { required }
  },
  formStep2: {
    phoneNumberIso: { required }
  },
  formStep3: {}
}

const searchInputRules = {
  country: { required },
  number: {
    numeric,
    required: helpers.withMessage('Required', required),
    numberPrefix: helpers.withMessage('Must not start with 0', value => {
      return value.split('')[0] != 0
    }),
    numberLength: helpers.withMessage('Must be minimum two characters', value => {
      return value.length > 1
    })
  }
}

const searchData = ref({
  country: 'GB',
  number: null
})

const stepValidationRules = computed(() => {
  if (formWizard.value?.currentStep) {
    return inputRules[`formStep${formWizard.value?.currentStep + 1}`]
  }
  return inputRules.formStep1
})

const v$ = useVuelidate(stepValidationRules, newNumber)
const v$Search = useVuelidate(searchInputRules, searchData)

async function validateStep() {
  const isStepCorrect = await v$.value.$validate()
  if (isStepCorrect) {
    formWizard.value.move(1) // Move to the next step
  }
}

async function searchNumber() {
  newNumber.value.countryCode = countriesList.find(country => country.code == searchData.value.country)?.code
  newNumber.value.dialingCode = countriesList.find(country => country.code == searchData.value.country)?.dialCode

  const dataToPost = {
    isoCountry: newNumber.value.countryCode,
    dialingCode: newNumber.value.dialingCode,
    number: parseInt(searchData.value.number)
  }

  const isStepCorrect = await v$Search.value.$validate()
  if (isStepCorrect) {
    try {
      purchasableNumbersApiRequest
        .send({
          method: 'GET',
          endpoint: '/v1/comms/phone-numbers/purchasable',
          params: dataToPost
        })
        .then(response => {
          if (response.success) {
            purchasablePhoneNumbers.value = response.data.slice(0, 5)
          } else {
            alertToast(
              t('ui.messages.failedToFetch', { item: t('ui.entities.comms.phoneNumber', 2) }),
              response.data.message,
              'error'
            )
          }
        })
    } catch (err) {
      alertToast(t('ui.messages.failedToFetch', { item: t('ui.entities.comms.phoneNumber', 2) }), err.message, 'error')
    }
  }
}

function selectNumber(index) {
  selectedNumberIndex.value = index
  newNumber.value.phoneNumberIso = purchasablePhoneNumbers.value[index].phoneNumber
  newNumber.value.location = purchasablePhoneNumbers.value[index].locality
  newNumber.value.canMakeCalls = purchasablePhoneNumbers.value[index].capabilities.voice
  newNumber.value.canSendSms = purchasablePhoneNumbers.value[index].capabilities.sms
}

const countriesList = [
  {
    name: useI18n().t('meta.countries.gb.label'),
    country: 'UK',
    code: 'GB',
    emoji: '🇬🇧',
    dialCode: 44,
    unicode: 'U+1F1EC U+1F1E7',
    image: 'https://cdn.jsdelivr.net/npm/country-flag-emoji-json@2.0.0/dist/images/GB.svg'
  }
  // {
  //   name: useI18n().t('meta.countries.us.label'),
  //   country: 'US',
  //   code: 'US',
  //   emoji: '🇺🇸',
  //   dialCode: 1,
  //   unicode: 'U+1F1FA U+1F1F8',
  //   image: 'https://cdn.jsdelivr.net/npm/country-flag-emoji-json@2.0.0/dist/images/US.svg'
  // }
]

const bundleOptions = [
  {
    name: 'Retain Automotive Ltd',
    sid: 'BU5ffa6487ddae7a650b45a14a8635cff4'
  }
]

const addressOptions = [
  {
    name: 'Retain Automotive Ltd',
    address: '37 Station Road, Harleston, Norfolk, United Kingdom, IP209ES',
    sid: 'AD981102f91a2c0ecd7d4f96afc4832ee0'
  }
]

async function submitNewNumber() {
  const isFormCorrect = await v$.value.$validate()
  if (!isFormCorrect) {
    return
  }

  Swal.fire({
    title: t('ui.messages.areYouSure'),
    icon: 'warning',
    reverseButtons: true,
    showCloseButton: true,
    showCancelButton: true,
    confirmButtonText: t('ui.messages.yesPurchaseNumber'),
    confirmButtonColor: 'var(--bg-brand-solid)',
    cancelButtonText: t('ui.actions.cancel')
  }).then(function (response) {
    if (response.isConfirmed) {
      const dataToPost = {
        callCenterId: newNumber.value.callCenterId,
        name: newNumber.value.name,
        location: newNumber.value.location,
        countryCode: newNumber.value.countryCode,
        textToSpeechName: newNumber.value.textToSpeechName,
        dealerships: newNumber.value.dealerships,
        dialingCode: newNumber.value.dialingCode,
        number: newNumber.value.phoneNumberIso.slice(newNumber.value.dialingCode.toString().length + 1),
        phoneNumberIso: newNumber.value.phoneNumberIso,
        addressSid: newNumber.value.addressSid,
        emergencyAddressSid: newNumber.value.addressSid,
        bundleSid: newNumber.value.bundleSid,
        canMakeCalls: newNumber.value.canMakeCalls,
        canSendSms: newNumber.value.canSendSms
      }

      try {
        purchaseNumberApiRequest
          .send({
            method: 'POST',
            endpoint: '/v1/comms/phone-numbers/purchase',
            data: JSON.stringify(dataToPost)
          })
          .then(response => {
            if (response.success) {
              alertToast(t('ui.messages.added'), null, 'success')
              emit('completed')
            }
          })
      } catch (err) {
        alertToast(t('ui.messages.failedToPurchase'), err.message, 'error')
      }
    }
  })
}

watch(
  () => newNumber.value,
  () => {
    emit('form-updated')
  },
  { deep: true }
)
</script>

<style></style>
